import React, { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { Table, Tag, Button, Input, Space, Tooltip, Tabs, Switch } from "antd";
import {
  EditOutlined,
  PhoneOutlined,
  MailOutlined,
  UserOutlined,
} from "@ant-design/icons";
import { all_routes } from "../../../router/all_routes";
import { showToast } from "../../../../core/data/redux/slices/ToastSlice";
import api from "../../../../core/axios/axiosInstance";
import { useDebounce } from "../../../../core/hooks/useDebounce";
import { updateCompanyInCache } from "../../../../core/data/redux/slices/adminCompanySlice";
import EditCompanyOffcanvas from "./EditCompanyOffcanvas";
import EditAgentOffcanvas from "./EditAgentOffcanvas";

const CompanyDetails = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const initialCompany = location.state?.company || null;
  const [companyDetails, setCompanyDetails] = useState(initialCompany);

  const [agents, setAgents] = useState([]);
  const [agentsLoading, setAgentsLoading] = useState(false);
  const [agentsPagination, setAgentsPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });
  const [searchText, setSearchText] = useState("");
  const debouncedSearch = useDebounce(searchText, 300);
  const [editCompanyVisible, setEditCompanyVisible] = useState(false);
  const [editAgentVisible, setEditAgentVisible] = useState(false);
  const [selectedAgent, setSelectedAgent] = useState(null);
  const [toggleLoading, setToggleLoading] = useState(false);
  const [accountToggleLoading, setAccountToggleLoading] = useState(false);
  const [agentLoadingToggles, setAgentLoadingToggles] = useState({});
  const [localExtensionStatus, setLocalExtensionStatus] = useState(
    initialCompany?.extensionStatus || false
  );
  const [localAccountStatus, setLocalAccountStatus] = useState(
    initialCompany?.accountStatus || "active"
  );

  // Fetch agents when component mounts, pagination changes or debounced search changes
  useEffect(() => {
    if (companyDetails?._id) {
      fetchAgents(
        companyDetails._id,
        agentsPagination.current,
        agentsPagination.pageSize,
        debouncedSearch
      );
    }
    // eslint-disable-next-line
  }, [
    companyDetails?._id,
    agentsPagination.current,
    agentsPagination.pageSize,
    // Note: we do not include debouncedSearch here intentionally because we handle it separately
  ]);

  // When debounced search term changes, reset to first page (or fetch directly if already on page 1)
  useEffect(() => {
    if (!companyDetails?._id) return;

    // If page is not 1, set it to 1 which will trigger the main effect to fetch using debouncedSearch
    if (agentsPagination.current !== 1) {
      setAgentsPagination((prev) => ({ ...prev, current: 1 }));
    } else {
      // If already on page 1, fetch directly with the debounced search term
      fetchAgents(
        companyDetails._id,
        1,
        agentsPagination.pageSize,
        debouncedSearch
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearch]);

  // keep local switch state in sync when company details change
  useEffect(() => {
    setLocalExtensionStatus(Boolean(companyDetails?.extensionStatus));
    setLocalAccountStatus(companyDetails?.accountStatus || "active");
  }, [companyDetails?.extensionStatus, companyDetails?.accountStatus]);
  const fetchAgents = async (adminId, page = 1, limit = 10, search = "") => {
    setAgentsLoading(true);
    try {
      const response = await api.post("/superAdmin/allAgentsOfCompanyAdmin", {
        adminId,
        page,
        limit,
        search,
      });
      if (response.data.status === "success") {
        setAgents(response.data.data);
        setAgentsPagination({
          current: response.data.page,
          pageSize: response.data.limit,
          total: response.data.totalAgents,
        });
      }
    } catch (error) {
      dispatch(
        showToast({
          heading: "Error",
          message: "Failed to fetch agents",
          variant: "danger",
        })
      );
    } finally {
      setAgentsLoading(false);
    }
  };

  const handleAgentsTableChange = (pag) => {
    setAgentsPagination({ ...pag, total: pag.total });
  };

  const handleSearch = () => {
    setAgentsPagination((prev) => ({ ...prev, current: 1 }));
    if (companyDetails?._id) {
      fetchAgents(companyDetails._id, 1, agentsPagination.pageSize, searchText);
    }
  };

  const handleEditCompany = () => {
    setEditCompanyVisible(true);
  };

  const handleToggleExtensionStatus = async (checked) => {
    if (!companyDetails?._id) return;
    setToggleLoading(true);
    try {
      // Call backend to update only the status. Backend will validate required fields when activating.
      // Include current extension fields so backend can validate activation
      const response = await api.put("/superAdmin/editCompanyAdminAndAgent", {
        userId: companyDetails._id,
        extensionNumber: companyDetails.extensionNumber,
        telephone: companyDetails.telephone,
        sipSecret: companyDetails.sipSecret,
        status: checked,
      });

      if (response.data.status === "success") {
        // Allow animation to complete before refreshing
        await new Promise((resolve) => setTimeout(resolve, 800));

        // Refetch company details from server to reflect any DB-side changes
        const detailsRes = await api.post(
          "/superAdmin/companyAdminDetailsById",
          {
            adminId: companyDetails._id,
          }
        );
        if (detailsRes.data.status === "success") {
          const updatedCompany = detailsRes.data.data;
          setCompanyDetails(updatedCompany);
          dispatch(
            updateCompanyInCache({
              companyId: updatedCompany._id,
              updates: updatedCompany,
            })
          );
          // Update local switch state and show success toast
          setLocalExtensionStatus(Boolean(updatedCompany.extensionStatus));
          navigate(location.pathname, {
            replace: true,
            state: {
              company: updatedCompany,
            },
          });
          dispatch(
            showToast({
              heading: "Success",
              message: updatedCompany.extensionStatus
                ? "Extension activated successfully"
                : "Extension deactivated successfully",
              variant: "success",
            })
          );
        }
      }
    } catch (error) {
      const msg = error?.response?.data?.error || "Failed to update status";
      dispatch(
        showToast({
          heading: "Error",
          message: msg,
          variant: "danger",
        })
      );
      // revert switch state
      setLocalExtensionStatus(Boolean(companyDetails?.extensionStatus));
    } finally {
      setToggleLoading(false);
    }
  };

  const handleToggleAccountStatus = async (checked) => {
    if (!companyDetails?._id) return;
    setAccountToggleLoading(true);
    try {
      const newStatus = checked ? "active" : "suspended";
      const endpoint = checked ? "/deleteUser/activate" : "/deleteUser/suspend";

      const response = await api.post(endpoint, {
        user_id: companyDetails._id,
      });

      if (response.data.status === "success") {
        // Allow animation to complete before refreshing
        // await new Promise((resolve) => setTimeout(resolve, 800));

        // Refetch company details from server
        const detailsRes = await api.post(
          "/superAdmin/companyAdminDetailsById",
          {
            adminId: companyDetails._id,
          }
        );
        if (detailsRes.data.status === "success") {
          const updatedCompany = detailsRes.data.data;
          setCompanyDetails(updatedCompany);
          navigate(location.pathname, {
            replace: true,
            state: {
              company: updatedCompany,
            },
          });
          fetchAgents(
            updatedCompany._id,
            1,
            agentsPagination.pageSize,
            searchText
          );
          dispatch(
            showToast({
              heading: "Success",
              message: response.data.message,
              variant: "success",
            })
          );
        }
      }
    } catch (error) {
      const msg =
        error?.response?.data?.message || "Failed to update account status";
      dispatch(
        showToast({
          heading: "Error",
          message: msg,
          variant: "danger",
        })
      );
      // revert switch state
      setLocalAccountStatus(companyDetails?.accountStatus || "active");
    } finally {
      setAccountToggleLoading(false);
    }
  };

  const handleEditAgent = (agent) => {
    setSelectedAgent(agent);
    setEditAgentVisible(true);
  };

  const handleCompanyOffcanvasClose = async (updated = false) => {
    setEditCompanyVisible(false);

    // Refetch company details if updated
    if (updated && companyDetails?._id) {
      try {
        const response = await api.post("/superAdmin/companyAdminDetailsById", {
          adminId: companyDetails._id,
        });
        if (response.data.status === "success") {
          const updatedCompany = response.data.data;
          setCompanyDetails(updatedCompany);
          navigate(location.pathname, {
            replace: true,
            state: {
              company: updatedCompany,
            },
          });

          // Update in Redux cache
          dispatch(
            updateCompanyInCache({
              companyId: updatedCompany._id,
              updates: updatedCompany,
            })
          );
          await fetchAgents(
            updatedCompany._id,
            1,
            agentsPagination.pageSize,
            searchText
          );
        }
      } catch (error) {
        console.error("Error refetching company details:", error);
      }
    }
  };

  const handleAgentOffcanvasClose = async (updated = false) => {
    setEditAgentVisible(false);
    setSelectedAgent(null);

    // Refetch agents list if updated
    if (updated && companyDetails?._id) {
      fetchAgents(
        companyDetails._id,
        agentsPagination.current,
        agentsPagination.pageSize,
        searchText
      );
    }
  };

  const handleToggleAgentAccountStatus = async (agentId, currentStatus) => {
    const toggleKey = `agent-account-${agentId}`;
    setAgentLoadingToggles((prev) => ({ ...prev, [toggleKey]: true }));

    try {
      const newStatus = currentStatus === "active" ? "suspended" : "active";
      const endpoint =
        newStatus === "active" ? "/deleteUser/activate" : "/deleteUser/suspend";

      const response = await api.post(endpoint, {
        user_id: agentId,
      });

      if (response.data.status === "success") {
        // Allow animation to complete before refreshing
        await new Promise((resolve) => setTimeout(resolve, 800));

        dispatch(
          showToast({
            heading: "Success",
            message: response.data.message,
            variant: "success",
          })
        );
        // Refresh agents list
        await fetchAgents(
          companyDetails._id,
          agentsPagination.current,
          agentsPagination.pageSize,
          searchText
        );
      }
    } catch (error) {
      const msg =
        error?.response?.data?.message ||
        "Failed to update agent account status";
      dispatch(
        showToast({
          heading: "Error",
          message: msg,
          variant: "danger",
        })
      );
    } finally {
      setAgentLoadingToggles((prev) => {
        const newState = { ...prev };
        delete newState[toggleKey];
        return newState;
      });
    }
  };

  const handleToggleAgentExtensionStatus = async (agentId, agent, checked) => {
    const toggleKey = `agent-extension-${agentId}`;
    setAgentLoadingToggles((prev) => ({ ...prev, [toggleKey]: true }));

    try {
      const response = await api.put("/superAdmin/editCompanyAdminAndAgent", {
        userId: agentId,
        extensionNumber: agent.extensionNumber,
        telephone: agent.telephone,
        sipSecret: agent.sipSecret,
        status: checked,
      });

      if (response.data.status === "success") {
        // Allow animation to complete before refreshing
        await new Promise((resolve) => setTimeout(resolve, 800));

        dispatch(
          showToast({
            heading: "Success",
            message: checked
              ? "Agent extension activated successfully"
              : "Agent extension deactivated successfully",
            variant: "success",
          })
        );
        // Refresh agents list
        await fetchAgents(
          companyDetails._id,
          agentsPagination.current,
          agentsPagination.pageSize,
          searchText
        );
      }
    } catch (error) {
      const msg =
        error?.response?.data?.error ||
        "Failed to update agent extension status";
      dispatch(
        showToast({
          heading: "Error",
          message: msg,
          variant: "danger",
        })
      );
    } finally {
      setAgentLoadingToggles((prev) => {
        const newState = { ...prev };
        delete newState[toggleKey];
        return newState;
      });
    }
  };

  const agentColumns = [
    {
      title: "Name",
      key: "name",
      render: (_, record) => (
        <span>
          <UserOutlined style={{ marginRight: 8 }} />
          {record.firstname ? `${record.firstname} ${record.lastname}` : "N/A"}
        </span>
      ),
    },
    {
      title: "Email",
      dataIndex: "email",
      key: "email",
      render: (email) => (
        <span>
          <MailOutlined style={{ marginRight: 8 }} />
          {email}
        </span>
      ),
    },
    {
      title: "Extension Status",
      dataIndex: "extensionStatus",
      key: "extensionStatus",
      render: (extensionStatus, record) => {
        const toggleKey = `agent-extension-${record._id}`;
        return (
          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <Switch
              checked={extensionStatus}
              loading={agentLoadingToggles[toggleKey]}
              onChange={(checked) =>
                handleToggleAgentExtensionStatus(record._id, record, checked)
              }
              checkedChildren={"Active"}
              unCheckedChildren={"Inactive"}
              size="large"
            />
          </div>
        );
      },
    },
    {
      title: "Account Status",
      dataIndex: "accountStatus",
      key: "accountStatus",
      render: (accountStatus, record) => {
        const toggleKey = `agent-account-${record._id}`;
        return (
          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <Switch
              checked={accountStatus === "active"}
              loading={agentLoadingToggles[toggleKey]}
              onChange={() =>
                handleToggleAgentAccountStatus(record._id, accountStatus)
              }
              checkedChildren={"Active"}
              unCheckedChildren={"Suspended"}
              size="large"
            />
          </div>
        );
      },
    },
    {
      title: "Extension",
      dataIndex: "extensionNumber",
      key: "extensionNumber",
      render: (ext) => <Tag color="green">{ext || "N/A"}</Tag>,
    },
    {
      title: "Telephone",
      dataIndex: "telephone",
      key: "telephone",
      render: (phone) => (
        <span>
          <PhoneOutlined style={{ marginRight: 8 }} />
          {phone || "N/A"}
        </span>
      ),
    },
    {
      title: "Created",
      dataIndex: "createdAt",
      key: "createdAt",
      render: (date) => new Date(date).toLocaleDateString(),
    },
    {
      title: "Actions",
      key: "actions",
      render: (_, record) => (
        <Tooltip title="Edit Agent">
          <Button
            icon={<EditOutlined />}
            size="small"
            onClick={() => handleEditAgent(record)}
          >
            Edit
          </Button>
        </Tooltip>
      ),
    },
  ];

  const tabItems = [
    {
      key: "agents",
      label: "Agents",
      children: (
        <div>
          <div className="d-flex justify-content-between align-items-center mb-3">
            <h5 className="fw-semibold mb-0">
              Agents List ({agentsPagination.total})
            </h5>
            <Space>
              <Input.Search
                placeholder="Search agents..."
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                onSearch={handleSearch}
                style={{ width: 300 }}
                allowClear
              />
            </Space>
          </div>
          <Table
            columns={agentColumns}
            dataSource={agents}
            rowKey="_id"
            loading={agentsLoading}
            pagination={{
              current: agentsPagination.current,
              pageSize: agentsPagination.pageSize,
              total: agentsPagination.total,
              showSizeChanger: true,
            }}
            onChange={handleAgentsTableChange}
            bordered
          />
        </div>
      ),
    },
  ];

  if (!companyDetails) {
    return (
      <div className="page-wrapper">
        <div className="content">
          <div className="text-center">
            <p>No company details available</p>
            <Button onClick={() => navigate(all_routes.adminCompaniesList)}>
              Back to Companies
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="page-wrapper">
      <div className="content">
        <div className="row">
          <div className="col-md-12">
            {/* Contact Head - Similar to customer details */}
            <div className="contact-head">
              <div className="row align-items-center">
                <div className="col-sm-6">
                  <ul className="contact-breadcrumb">
                    <li>
                      <Link to={all_routes.adminCompaniesList}>
                        <i className="ti ti-arrow-narrow-left" />
                        Companies
                      </Link>
                    </li>
                    <li>
                      {companyDetails.firstname
                        ? `${companyDetails.firstname} ${companyDetails.lastname}`
                        : "N/A"}
                    </li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Company Info Card - Similar to customer card */}
            <div className="card">
              <div className="card-body pb-2">
                <div className="d-flex align-items-center justify-content-between flex-wrap">
                  <div className="d-flex align-items-start  mb-2">
                    <span className="avatar avatar-xl bg-primary flex-shrink-0 me-3">
                      <span className="avatar-title text-white">
                        {companyDetails.firstname
                          ? companyDetails.firstname[0]
                          : "N"}
                        {companyDetails.lastname
                          ? companyDetails.lastname[0]
                          : "A"}
                      </span>
                    </span>
                    <div>
                      <h5 className="mb-0 mt-1">
                        {companyDetails.firstname
                          ? `${companyDetails.firstname} ${companyDetails.lastname}`
                          : "N/A"}
                      </h5>
                      <div className="d-flex mt-2">
                        <span className="me-1 text-dark">
                          <MailOutlined style={{ marginRight: 4 }} />
                          {companyDetails.email}
                        </span>
                      </div>
                    </div>
                  </div>
                  <div className="contacts-action align-items-center mb-2">
                    <div className="d-flex align-items-end gap-2">
                      <div className="d-flex flex-column align-items-center">
                        <small className="text-muted mb-1">Extension</small>
                        <Switch
                          checked={localExtensionStatus}
                          loading={toggleLoading}
                          onChange={handleToggleExtensionStatus}
                          checkedChildren={"Active"}
                          unCheckedChildren={"Inactive"}
                          title={
                            localExtensionStatus
                              ? "Deactivate extension"
                              : "Activate extension"
                          }
                        />
                      </div>
                      <div className="d-flex flex-column align-items-center">
                        <small className="text-muted mb-1">Account</small>
                        <Switch
                          checked={localAccountStatus === "active"}
                          loading={accountToggleLoading}
                          onChange={handleToggleAccountStatus}
                          checkedChildren={"Active"}
                          unCheckedChildren={"Suspended"}
                          title={
                            localAccountStatus === "active"
                              ? "Suspend account"
                              : "Activate account"
                          }
                        />
                      </div>
                      <Button
                        type="primary"
                        icon={<EditOutlined />}
                        onClick={handleEditCompany}
                      >
                        Edit Company
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Company Details Card */}
            <div className="card mb-3">
              <div className="card-body">
                <div className="row">
                  <div className="col-md-2">
                    <div className="mb-3">
                      <label className="text-muted small">
                        Extension Number
                      </label>
                      <div className="fw-semibold">
                        <Tag color="blue">
                          {companyDetails.extensionNumber || "N/A"}
                        </Tag>
                      </div>
                    </div>
                  </div>
                  <div className="col-md-2">
                    <div className="mb-3">
                      <label className="text-muted small">
                        Telephone Number
                      </label>
                      <div className="fw-semibold">
                        <PhoneOutlined style={{ marginRight: 8 }} />
                        {companyDetails.telephone || "N/A"}
                      </div>
                    </div>
                  </div>
                  <div className="col-md-2">
                    <div className="mb-3">
                      <label className="text-muted small">Created At</label>
                      <div className="fw-semibold">
                        {new Date(
                          companyDetails.createdAt
                        ).toLocaleDateString()}
                      </div>
                    </div>
                  </div>
                  <div className="col-md-2">
                    <div className="mb-3">
                      <label className="text-muted small">
                        Extension Status
                      </label>
                      <div className="fw-semibold">
                        <Tag
                          color={
                            companyDetails.extensionStatus ? "green" : "red"
                          }
                        >
                          {companyDetails.extensionStatus
                            ? "Active"
                            : "Inactive"}
                        </Tag>
                      </div>
                    </div>
                  </div>
                  <div className="col-md-2">
                    <div className="mb-3">
                      <label className="text-muted small">Account Status</label>
                      <div className="fw-semibold">
                        <Tag
                          color={
                            companyDetails.accountStatus === "active"
                              ? "green"
                              : companyDetails.accountStatus === "suspended"
                              ? "orange"
                              : "red"
                          }
                        >
                          {companyDetails.accountStatus === "active"
                            ? "Active"
                            : companyDetails.accountStatus === "suspended"
                            ? "Suspended"
                            : "Deactivated"}
                        </Tag>
                      </div>
                    </div>
                  </div>
                  <div className="col-md-2">
                    <div className="mb-3">
                      <label className="text-muted small">
                        Phone number(s)
                      </label>
                      <div className="fw-semibold">
                        <PhoneOutlined style={{ marginRight: 8 }} />
                        {companyDetails.phoneNumbers?.[0]
                          ? `${
                              companyDetails.phoneNumbers[0].countryCode || ""
                            } ${
                              companyDetails.phoneNumbers[0].number || ""
                            }`.trim()
                          : "No phone numbers"}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Tabs */}
            <div className="card">
              <div className="card-body">
                <Tabs defaultActiveKey="agents" items={tabItems} />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Edit Company Offcanvas */}
      {editCompanyVisible && (
        <EditCompanyOffcanvas
          visible={editCompanyVisible}
          onClose={handleCompanyOffcanvasClose}
          company={companyDetails}
        />
      )}

      {/* Edit Agent Offcanvas */}
      {editAgentVisible && selectedAgent && (
        <EditAgentOffcanvas
          visible={editAgentVisible}
          onClose={handleAgentOffcanvasClose}
          agent={selectedAgent}
        />
      )}
    </div>
  );
};

export default CompanyDetails;
